import {Component} from '@angular/core';
import {
    FraudReportApiService,
    FraudReportType,
    OauthCallbackState,
    OauthCallbackType,
    PasswordRecoveryApiService,
    RefundApiService,
} from '@npck/af-frontend-api';
import {NESTED_TAB, RouterService, TAB} from '@npck/libs/commons';
import {FRAUD_REPORT_ID, FRAUD_REPORT_TYPE, INCIDENT_ID, REFUND_ID, TOKEN} from '../../domain/query-params.keys';
import {Path} from '../../path';
import {ANALYTIC_FRAUD_REPORTS_TAB} from '../analytics/analytics.component';

@Component({
    selector: 'af-oauth-callback',
    standalone: false,
    templateUrl: './oauth-callback.component.html',
    styleUrl: './oauth-callback.component.scss',
})
export class OauthCallbackComponent {
    constructor(
        private refundApiService: RefundApiService,
        private passwordRecoveryApiService: PasswordRecoveryApiService,
        private fraudReportApiService: FraudReportApiService,
        private routerService: RouterService,
    ) {
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        const state = url.searchParams.get('state');
        const parsedState: OauthCallbackState = JSON.parse(state);

        switch (parsedState.oauthCallbackType) {
            case OauthCallbackType.REFUND_RESOLUTION_SIGN:
                this.processRefundResolutionSignCallback(code, parsedState);
                break;
            case OauthCallbackType.REFUND_REJECTION:
                this.processRefundRejectionCallback(code, parsedState);
                break;
            case OauthCallbackType.PASSWORD_RECOVERY:
                this.processPasswordRecoveryCallback(code);
                break;
            case OauthCallbackType.FRAUD_REPORTS:
                this.processFraudReportCallback(code, parsedState);
                break;
        }
    }

    private processRefundResolutionSignCallback(code: string | null, state: OauthCallbackState): void {
        if (code) {
            this.refundApiService.signResolutionForm({
                code: code,
                id: state.id,
                redirectUri: state.redirectUri,
            })
                .subscribe({
                    next: () => this.navigateToRefund(state.id),
                    error: () => this.navigateToRefund(state.id),
                });
        } else {
            this.routerService.navigate(Path.INCIDENT_DETAILS, {
                pathParams: {[INCIDENT_ID]: state.incidentId},
                queryParams: {[NESTED_TAB]: 'tabRefunds'},
            });
        }
    }

    private processRefundRejectionCallback(code: string | null, state: OauthCallbackState): void {
        if (code) {
            this.refundApiService.completeRefundRejection({
                code: code,
                id: state.id,
                redirectUri: state.redirectUri,
            })
                .subscribe({
                    next: () => this.navigateToRefund(state.id),
                    error: () => this.navigateToRefund(state.id),
                });
        } else {
            this.navigateToRefund(state.id);
        }
    }

    private processFraudReportCallback(code: string | null, state: OauthCallbackState): void {
        if (code) {
            this.fraudReportApiService.saveSignedFraudReport({
                code: code,
                id: state.id,
                redirectUri: state.redirectUri,
            })
                .subscribe((response) => {
                    this.routerService.navigate(Path.ANALYTICS_FRAUD_REPORT_DETAILS, {
                        pathParams: {
                            [FRAUD_REPORT_TYPE]: response.reportType,
                            [FRAUD_REPORT_ID]: response.id,
                        },
                        queryParams: {
                            [TAB]: ANALYTIC_FRAUD_REPORTS_TAB,
                            [NESTED_TAB]: 'tabFraudReportFiles',
                        },
                    });
                });
        } else {
            this.routerService.navigate(Path.ANALYTICS_FRAUD_REPORTS, {
                pathParams: {
                    [FRAUD_REPORT_TYPE]: FraudReportType.NBRK_PAYMENT_FRAUD,
                },
                queryParams: {
                    [TAB]: ANALYTIC_FRAUD_REPORTS_TAB
                },
            });
        }
    }

    private processPasswordRecoveryCallback(code: string | null): void {
        this.passwordRecoveryApiService.completePasswordRecoveryByBiometric({code: code, frontendHost: window.location.host})
            .subscribe(response =>
                this.routerService.navigate(Path.RESET_PASSWORD, {queryParams: {[TOKEN]: response.token}}));
    }

    private navigateToRefund(refundId: string): void {
        this.routerService.navigate(Path.REFUND_DETAILS, {pathParams: {[REFUND_ID]: refundId}});
    }
}
